import { Navigate, createBrowserRouter } from 'react-router-dom';
import { LoginPage } from '@/features/auth/LoginPage';
import { SignupPage } from '@/features/auth/SignupPage';
import { AcceptInvitePage } from '@/features/auth/AcceptInvitePage';
import { InboxPage } from '@/features/inbox/InboxPage';
import { TicketPage } from '@/features/inbox/TicketPage';
import { ChatsPage } from '@/features/chats/ChatsPage';
import { CustomersPage } from '@/features/customers/CustomersPage';
import { CustomerPage } from '@/features/customers/CustomerPage';
import { KnowledgeBasePage } from '@/features/kb/KnowledgeBasePage';
import { ArticlePage } from '@/features/kb/ArticlePage';
import { AnalyticsPage } from '@/features/analytics/AnalyticsPage';
import { AiAgentsPage } from '@/features/ai-agents/AiAgentsPage';
import { WorkflowsPage } from '@/features/workflows/WorkflowsPage';
import { SettingsLayout } from '@/features/settings/SettingsLayout';
import { ProfilePage } from '@/features/settings/ProfilePage';
import { AgentsPage } from '@/features/settings/AgentsPage';
import { TeamsPage } from '@/features/settings/TeamsPage';
import { AuditLogPage } from '@/features/settings/AuditLogPage';
import { AiSettingsPage } from '@/features/settings/AiSettingsPage';
import { EmailPage } from '@/features/settings/EmailPage';
import { WidgetsPage } from '@/features/settings/WidgetsPage';
import { ApiPage } from '@/features/settings/ApiPage';
import { NotFoundPage } from '@/features/NotFoundPage';
import { WorkspacePage } from '@/features/settings/WorkspacePage';
import { BillingPage } from '@/features/settings/BillingPage';
import { ClientsPage } from '@/features/platform/ClientsPage';
import { ClientDetailPage } from '@/features/platform/ClientDetailPage';
import { PlatformOverviewPage } from '@/features/platform/PlatformOverviewPage';
import { RequireAbility, RequireAuth } from './auth/AuthContext';
import { HomeRedirect, PlatformArea, RequireWorkspace } from './auth/RequireWorkspace';
import { AppShell } from './layout/AppShell';

export const router = createBrowserRouter(
  [
    { path: '/login', element: <LoginPage /> },
    { path: '/signup', element: <SignupPage /> },
    { path: '/accept-invite', element: <AcceptInvitePage /> },
    {
      path: '/',
      element: (
        <RequireAuth>
          <AppShell />
        </RequireAuth>
      ),
      children: [
        { index: true, element: <HomeRedirect /> },
        // Platform console (super admins), always outside any client workspace.
        {
          path: 'admin',
          element: (
            <RequireAbility I="manage" a="Workspace">
              <PlatformArea />
            </RequireAbility>
          ),
          children: [
            { index: true, element: <PlatformOverviewPage /> },
            { path: 'clients', element: <ClientsPage /> },
            { path: 'clients/:clientId', element: <ClientDetailPage /> },
          ],
        },
        // Everything below lives inside a client workspace (super admins must open one first).
        {
          element: <RequireWorkspace />,
          children: [
            { path: 'inbox', element: <InboxPage /> },
            { path: 'inbox/:ticketId', element: <TicketPage /> },
            { path: 'chats', element: <ChatsPage /> },
            { path: 'chats/:ticketId', element: <ChatsPage /> },
            { path: 'customers', element: <CustomersPage /> },
            { path: 'customers/:customerId', element: <CustomerPage /> },
            { path: 'kb', element: <KnowledgeBasePage /> },
            { path: 'kb/articles/new', element: <ArticlePage /> },
            { path: 'kb/articles/:articleId', element: <ArticlePage /> },
            { path: 'analytics', element: <AnalyticsPage /> },
            { path: 'ai-agents', element: <AiAgentsPage /> },
            { path: 'workflows', element: <WorkflowsPage /> },
            {
              path: 'settings',
              element: <SettingsLayout />,
              children: [
                { index: true, element: <Navigate to="profile" replace /> },
                { path: 'profile', element: <ProfilePage /> },
                { path: 'workspace', element: <WorkspacePage /> },
                { path: 'billing', element: <BillingPage /> },
                { path: 'agents', element: <AgentsPage /> },
                { path: 'teams', element: <TeamsPage /> },
                { path: 'ai', element: <AiSettingsPage /> },
                { path: 'widget', element: <WidgetsPage /> },
                { path: 'email', element: <EmailPage /> },
                { path: 'api', element: <ApiPage /> },
                { path: 'audit-log', element: <AuditLogPage /> },
              ],
            },
          ],
        },
        { path: '*', element: <NotFoundPage /> },
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);
